import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BillingPolicyReturn,
  RunDetail as RunDetailReturn,
  RunLineReturn,
} from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Divider } from '../common/Catalyst/divider';
import {
  Description,
  ErrorMessage,
  Field,
  Label,
} from '../common/Catalyst/fieldset';
import { Textarea } from '../common/Catalyst/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Text } from '../common/Catalyst/text';
import { Subheading } from '../common/Catalyst/heading';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  compareMoney,
  formatBillingDate,
  formatEpochSeconds,
  formatMoney,
  formatPercent,
} from '../utils/billingHelpers';
import { runStatus } from './enumLabels';

interface Props {
  hoaId: string;
  runId: string;
  canPost: boolean;
  canReverse: boolean;
}

/** "prorated 12/31 days", where a line was not charged in full. */
const prorationNote = (line: RunLineReturn): string | null => {
  const n = line.proration_numerator;
  const d = line.proration_denominator;
  if (n == null || d == null || n === d) return null;
  return `prorated ${n}/${d} days`;
};

/**
 * One run, its lines, and the two actions that matter.
 *
 * **The lines are what will post.** They were computed and stored at
 * generation time, not recomputed on read, which is what makes this
 * preview binding rather than indicative — so the screen says so.
 *
 * Post and reverse are deliberately separate buttons with separate
 * confirmations. Reversing names the entry count and the total, because
 * that is what is about to be undone across every homeowner in the
 * association.
 */
const RunDetail: React.FC<Props> = ({ hoaId, runId, canPost, canReverse }) => {
  const navigate = useNavigate();
  const [run, setRun] = useState<RunDetailReturn | null>(null);
  // The variance threshold is per-HOA policy, so the banner below reads
  // it rather than assuming the shipped default of 20.
  const [policy, setPolicy] = useState<BillingPolicyReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmPost, setConfirmPost] = useState(false);
  const [confirmReverse, setConfirmReverse] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await billingService.getRun({ runId });
      setRun(data);
      const { data: hoaPolicy } = await billingService.getHoaPolicy({
        hoaId,
      });
      setPolicy(hoaPolicy);
    } catch (err) {
      console.error('Error loading run:', err);
      setError(billingErrorMessage(err, "Couldn't load this billing run."));
    } finally {
      setLoading(false);
    }
  }, [runId, hoaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const post = async (): Promise<void> => {
    try {
      setBusy(true);
      setError(null);
      await billingService.postRun({ runId });
      setConfirmPost(false);
      setNotice('Posted. Every line is now a charge on a homeowner’s ledger.');
      await load();
    } catch (err) {
      console.error('Error posting run:', err);
      setError(billingErrorMessage(err));
      setConfirmPost(false);
    } finally {
      setBusy(false);
    }
  };

  const reverse = async (): Promise<void> => {
    if (!reason.trim()) {
      setReasonError('A reason is required — it stays on every entry.');
      return;
    }
    try {
      setBusy(true);
      setError(null);
      await billingService.reverseRun({
        runId,
        reverseRequest: { reason: reason.trim(), effective_date: null },
      });
      setConfirmReverse(false);
      setReason('');
      setNotice(
        'Reversed. Every charge this run made has an entry against it.'
      );
      await load();
    } catch (err) {
      console.error('Error reversing run:', err);
      setError(billingErrorMessage(err));
      setConfirmReverse(false);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !run) return <LoadingSpinner />;
  if (!run) {
    return (
      <ShowAlert
        alertType="error"
        alertTitle="Couldn't load this billing run"
        alertMessage={error ?? 'Please try again.'}
        isVisible
        onClose={() => setError(null)}
        timeout={0}
        isInline
      />
    );
  }

  const prorated = run.lines.filter((l) => prorationNote(l) !== null);
  // A manually generated run is never held, whatever its variance — the
  // guard only stops the scheduler. So a big jump on one a person asked
  // for goes by silently unless this says something.
  const overThreshold =
    !!run.variance_percent &&
    policy != null &&
    compareMoney(
      run.variance_percent.replace('-', ''),
      String(policy.variance_threshold_percent)
    ) > 0;

  return (
    <div>
      <Button plain onClick={() => navigate(`/hoa/${hoaId}/billing/runs`)}>
        ← All runs
      </Button>

      {error && (
        <div className="my-4">
          <ShowAlert
            alertType="error"
            alertTitle="That did not work"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={14000}
            isInline
          />
        </div>
      )}
      {notice && (
        <div className="my-4">
          <ShowAlert
            alertType="success"
            alertTitle="Done"
            alertMessage={notice}
            isVisible={!!notice}
            onClose={() => setNotice(null)}
            timeout={8000}
            isInline
          />
        </div>
      )}

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>
            {formatBillingDate(run.period_start)} –{' '}
            {formatBillingDate(run.period_end)}
          </Subheading>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge color={runStatus.color(run.status)}>
              {runStatus.label(run.status)}
            </Badge>
            {run.unattended && <Badge color="zinc">Scheduled</Badge>}
            <Text className="text-sm">
              Due {formatBillingDate(run.due_date)} · {run.charge_count}{' '}
              {run.charge_count === 1 ? 'home' : 'homes'} ·{' '}
              {formatMoney(run.total_amount)}
            </Text>
          </div>
          {run.posted_at && (
            <Text className="mt-1 text-sm">
              Posted {formatEpochSeconds(run.posted_at)}
            </Text>
          )}
        </div>

        <div className="flex gap-2">
          {canPost && run.status === 'pending' && (
            <Button color="sky" onClick={() => setConfirmPost(true)}>
              Post this run
            </Button>
          )}
          {canReverse && run.status === 'posted' && (
            <Button color="red" onClick={() => setConfirmReverse(true)}>
              Reverse this run
            </Button>
          )}
        </div>
      </div>

      {run.held_reason && (
        <div className="mt-4 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4">
          <Subheading>Held for review</Subheading>
          <Text className="mt-1">{run.held_reason}</Text>
          <Text className="mt-1 text-sm">
            The variance guard stopped this rather than posting it. Read the
            lines; if they are right, post it.
          </Text>
        </div>
      )}

      {!run.held_reason && overThreshold && (
        <div className="mt-4">
          <Text>
            This run&rsquo;s total moved {formatPercent(run.variance_percent)}{' '}
            against the previous period, past this association&rsquo;s{' '}
            {policy?.variance_threshold_percent}% threshold.{' '}
            <strong>It was not held</strong> — the guard only holds runs the
            scheduler generated, and this one was asked for by a person. Worth a
            look before posting.
          </Text>
        </div>
      )}

      <Divider className="my-6" />

      <Subheading level={4}>
        {run.status === 'pending' ? 'What will post' : 'What posted'}
      </Subheading>
      <Text className="mt-1">
        These lines were worked out and stored when the run was generated — they
        are not recomputed now.{' '}
        {run.status === 'pending'
          ? 'What you see here is exactly what will reach the ledger.'
          : 'They are the record of what reached the ledger.'}
        {prorated.length > 0 &&
          ` ${prorated.length} of them ${
            prorated.length === 1 ? 'is' : 'are'
          } prorated.`}
      </Text>

      <Table dense className="mt-4">
        <TableHead>
          <TableRow>
            <TableHeader>Home</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader className="text-right">Full period</TableHeader>
            <TableHeader className="text-right">Charged</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {run.lines.map((l) => {
            const note = prorationNote(l);
            return (
              <TableRow key={l.id}>
                <TableCell>{l.home_label ?? l.home_id}</TableCell>
                <TableCell>
                  {l.description}
                  {note && (
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      {note}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {formatMoney(l.base_amount)}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatMoney(l.amount)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog
        open={confirmPost}
        onClose={() => setConfirmPost(false)}
        size="lg"
      >
        <DialogTitle>Post this run?</DialogTitle>
        <DialogBody>
          <Text>
            This writes <strong>{run.charge_count}</strong> charges totalling{' '}
            <strong>{formatMoney(run.total_amount)}</strong> to{' '}
            {run.charge_count === 1 ? 'one homeowner’s' : 'homeowners’'}{' '}
            ledgers, due {formatBillingDate(run.due_date)}.
          </Text>
          <Text className="mt-2">
            Undoing it afterwards means reversing every one of them — and if
            coupons or statements have gone out by then, the damage is outside
            this system.
          </Text>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setConfirmPost(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="sky" onClick={post} disabled={busy}>
            {busy ? 'Posting…' : `Post ${run.charge_count} charges`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmReverse}
        onClose={() => setConfirmReverse(false)}
        size="lg"
      >
        <DialogTitle>Reverse this run?</DialogTitle>
        <DialogBody>
          <Text>
            This writes an opposing entry against each of the{' '}
            <strong>{run.charge_count}</strong> charges this run made, totalling{' '}
            <strong>{formatMoney(run.total_amount)}</strong>. Nothing is deleted
            — both the charge and its reversal stay on every homeowner&rsquo;s
            statement.
          </Text>
          <Field className="mt-4">
            <Label>Reason</Label>
            <Description>
              Required, and it goes onto every entry this writes.
            </Description>
            <Textarea
              rows={3}
              value={reason}
              invalid={!!reasonError}
              onChange={(e) => {
                setReason(e.target.value);
                setReasonError('');
              }}
            />
            {reasonError && <ErrorMessage>{reasonError}</ErrorMessage>}
          </Field>
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => setConfirmReverse(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button color="red" onClick={reverse} disabled={busy}>
            {busy ? 'Reversing…' : `Reverse ${run.charge_count} charges`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RunDetail;
